/**
 * ADM-31 操作日志：只读查询页（筛选 + 列表 + 详情抽屉 + 导出）。
 *
 * - 9 维筛选（spec 验收口径；风险级别由 bizType 敏感映射派生标签展示）
 * - 列表默认 20 / 上限 100 / operated_at 倒序 / 无操作列，编号点击开详情抽屉
 * - 详情抽屉：前值/后值 + 业务原因原文 + detail 结构化关联标识（安全审计上下文）
 * - 导出按钮按 ADM-31 EXPORT 权限显隐（统一导出任务模型）
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Button, DatePicker, Descriptions, Drawer, Input, Select, Space, Table, Tag, Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { useHasPermission } from '../../auth/HasPermission';
import {
  BIZ_TYPE_LABELS, operationLogsApi,
  type OperationLogDetail, type OperationLogListItem,
} from '../../api/operation-logs.api';
import type { Paginated } from '../../api/operation-logs.api';
import { useListExport } from '../../hooks/useListExport';
import { useGlobalCampusScope } from '../../auth/GlobalCampusContext';
import { useResizableColumns } from '../../hooks/useResizableColumns';

const { Title } = Typography;
const { RangePicker } = DatePicker;

interface LogFilters {
  operatorKeyword: string;
  bizType?: string;
  action?: string;
  bizId: string;
  result?: 'SUCCESS' | 'FAILED';
  range: [Dayjs | null, Dayjs | null] | null;
}

const INITIAL_FILTERS: LogFilters = {
  operatorKeyword: '', bizType: undefined, action: undefined, bizId: '',
  result: undefined, range: null,
};

/** 动作中文标签（常见动作；未知动作原样展示）。 */
const ACTION_LABELS: Record<string, string> = {
  view: '查看', create: '创建', update: '更新', remove: '删除', add: '新增',
  download: '下载', reverse: '冲正', bind: '绑定',
};

export function OperationLogPage() {
  const canExport = useHasPermission('ADM-31', 'EXPORT');
  const { exporting, runExport } = useListExport('operation-logs');
  // 全局校区范围：'ALL' 不携带 campusId（锁定模式，页内无校区筛选）
  const { scope } = useGlobalCampusScope();
  const activeCampusId = scope === 'ALL' ? undefined : scope;

  const [filters, setFilters] = useState<LogFilters>(INITIAL_FILTERS);
  const [applied, setApplied] = useState<LogFilters>(INITIAL_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Paginated<OperationLogListItem>>({
    items: [], total: 0, page: 1, pageSize: 20,
  });
  const [detail, setDetail] = useState<OperationLogDetail | null>(null);

  const { columns, components } = useResizableColumns<OperationLogListItem>([
    {
      title: '编号', dataIndex: 'id', width: 130, ellipsis: true,
      render: (value: string) => <a onClick={() => void loadDetail(value)}>{value.slice(0, 8)}</a>,
    },
    { title: '时间', dataIndex: 'operatedAt', width: 160, sorter: false, render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm:ss') },
    { title: '操作人', dataIndex: 'operatorName', width: 90, render: (v: string | null) => v ?? '-' },
    { title: '角色', dataIndex: 'role', width: 90, render: (v: string | null) => v ?? '-' },
    { title: '校区', dataIndex: 'campusName', width: 100, render: (v: string | null) => v ?? '总部' },
    {
      title: '业务类型', dataIndex: 'bizType', width: 130,
      render: (v: string) => BIZ_TYPE_LABELS[v] ?? v,
    },
    { title: '业务编号', dataIndex: 'bizId', width: 130, ellipsis: true },
    { title: '动作', dataIndex: 'action', width: 80, render: (v: string) => ACTION_LABELS[v] ?? v },
    { title: '业务原因', dataIndex: 'reason', width: 140, ellipsis: true, render: (v: string | null) => v ?? '-' },
    { title: 'IP/设备', dataIndex: 'ipDeviceSummary', width: 130, ellipsis: true, render: (v: string | null) => v ?? '-' },
    {
      title: '结果', dataIndex: 'result', width: 80,
      render: (v: 'SUCCESS' | 'FAILED') => (
        <Tag color={v === 'SUCCESS' ? 'green' : 'red'}>{v === 'SUCCESS' ? '成功' : '失败'}</Tag>
      ),
    },
  ]);

  const buildQuery = useCallback(
    (f: LogFilters, p: number, size: number): Record<string, unknown> => {
      const query: Record<string, unknown> = {
        page: p, pageSize: size,
      };
      if (f.operatorKeyword.trim()) query.operatorKeyword = f.operatorKeyword.trim();
      if (f.bizType) query.bizType = f.bizType;
      if (f.action?.trim()) query.action = f.action.trim();
      if (f.bizId.trim()) query.bizId = f.bizId.trim();
      if (f.result) query.result = f.result;
      if (activeCampusId) query.campusId = activeCampusId;
      if (f.range?.[0]) query.operatedFrom = f.range[0].startOf('day').toISOString();
      if (f.range?.[1]) query.operatedTo = f.range[1].endOf('day').toISOString();
      return query;
    },
    [activeCampusId],
  );

  const load = useCallback(async (f: LogFilters, p: number, size: number) => {
    setLoading(true);
    try {
      const result = await operationLogsApi.listOperationLogs(
        buildQuery(f, p, size) as never,
      );
      setData(result);
    } finally {
      setLoading(false);
    }
  }, [buildQuery]);

  useEffect(() => { void load(applied, page, pageSize); }, [applied, page, pageSize, load]);

  const loadDetail = useCallback(async (id: string) => {
    setDetail(await operationLogsApi.getOperationLogDetail(id));
  }, []);

  const handleSearch = () => {
    setPage(1);
    setApplied({ ...filters });
  };
  const handleReset = () => {
    setFilters(INITIAL_FILTERS);
    setPage(1);
    setApplied(INITIAL_FILTERS);
  };

  /** 当前生效筛选（导出快照与列表一致）。 */
  const currentQuery = useCallback(() => buildQuery(applied, 1, 20), [applied, buildQuery]);

  return (
    <div>
      <div className="page-header-row">
        <div>
          <Title level={3}>操作日志</Title>
          <p className="page-header-desc">关键业务操作的操作人、动作与前后值，只读可追溯。</p>
        </div>
      </div>
      <div className="page-filter-card">
        <Space wrap size={12}>
          <Input.Search
            placeholder="操作人 / 业务编号" style={{ width: 220 }} allowClear
            value={filters.operatorKeyword}
            onChange={(e) => setFilters({ ...filters, operatorKeyword: e.target.value })}
            onSearch={handleSearch}
          />
          <Select
            placeholder="业务类型" allowClear style={{ width: 150 }}
            value={filters.bizType}
            onChange={(v) => setFilters({ ...filters, bizType: v })}
            options={Object.entries(BIZ_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
          />
          <Select
            placeholder="动作" allowClear showSearch style={{ width: 120 }}
            value={filters.action}
            onChange={(v) => setFilters({ ...filters, action: v })}
            options={Object.entries(ACTION_LABELS).map(([value, label]) => ({ value, label }))}
          />
          <Input
            placeholder="业务编号" allowClear style={{ width: 200 }}
            value={filters.bizId}
            onChange={(e) => setFilters({ ...filters, bizId: e.target.value })}
          />
          <RangePicker
            value={filters.range}
            onChange={(range) => setFilters({ ...filters, range })}
          />
          <Select
            placeholder="结果" allowClear style={{ width: 100 }}
            value={filters.result}
            onChange={(v) => setFilters({ ...filters, result: v })}
            options={[
              { value: 'SUCCESS', label: '成功' },
              { value: 'FAILED', label: '失败' },
            ]}
          />
          <Button type="primary" icon={<ReloadOutlined />} onClick={handleSearch}>查询</Button>
          <Button onClick={handleReset}>重置</Button>
          {canExport && (
            <Button
              icon={<DownloadOutlined />} loading={exporting}
              onClick={() => void runExport(currentQuery())}
            >
              导出
            </Button>
          )}
        </Space>
      </div>
      <div className="page-content-card">
        <Table
          rowKey="id"
          loading={loading}
          dataSource={data.items}
          columns={columns as ColumnsType<OperationLogListItem>}
          components={components}
          pagination={{
            current: page, pageSize, total: data.total,
            showSizeChanger: true, pageSizeOptions: ['20', '50', '100'],
            onChange: (p, s) => { setPage(p); setPageSize(s); },
          }}
          locale={{ emptyText: '暂无记录' }}
        />
      </div>
      <Drawer
        open={!!detail}
        title={`操作详情 · ${detail?.id.slice(0, 8) ?? ''}`}
        width={520}
        onClose={() => setDetail(null)}
      >
        {detail && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="时间">{dayjs(detail.operatedAt).format('YYYY-MM-DD HH:mm:ss')}</Descriptions.Item>
            <Descriptions.Item label="操作人">
              {detail.operatorName ?? '-'}
              {detail.role ? `（${detail.role}）` : ''}
            </Descriptions.Item>
            <Descriptions.Item label="校区">{detail.campusName ?? '总部'}</Descriptions.Item>
            <Descriptions.Item label="业务类型">
              {BIZ_TYPE_LABELS[detail.bizType] ?? detail.bizType}
            </Descriptions.Item>
            <Descriptions.Item label="业务编号">{detail.bizId}</Descriptions.Item>
            <Descriptions.Item label="动作">{ACTION_LABELS[detail.action] ?? detail.action}</Descriptions.Item>
            <Descriptions.Item label="结果">
              <Tag color={detail.result === 'SUCCESS' ? 'green' : 'red'}>
                {detail.result === 'SUCCESS' ? '成功' : '失败'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="业务原因">{detail.reason ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="前值">{detail.beforeValue ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="后值">{detail.afterValue ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="IP/设备摘要">{detail.ipDeviceSummary ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="关联业务标识">
              {detail.detail
                ? Object.entries(detail.detail)
                    .filter(([key]) => !['traceId', 'organizationId'].includes(key))
                    .map(([key, value]) => `${key}: ${String(value)}`)
                    .join('；') || '-'
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="安全日志（trace）">
              {typeof detail.detail?.traceId === 'string' && detail.detail.traceId
                ? detail.detail.traceId
                : '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </div>
  );
}
